import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import { Button, Icon, Tooltip } from '@sgme/ui';
import { FormattedMessage } from 'react-intl';

import type { NullableLegProperties } from '@/api/trade-capture/option/checkOption.ts';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { UnpinIcon } from '@/App/tiles/Option/strategy/UnpinIcon.tsx';
import { getPremiumFromQuoteLegs } from '@/App/tiles/Option/utils/premiumUtils.ts';
import { selectOptionQuoteLegs } from '@/store/option/selectors/optionSelectors.ts';
import type { FxOption } from '@/store/option/state.ts';
import type { IntlMessageId } from '@/types/intl';

export function PinAllPremiums() {
  const [checkOption] = useCheckOption();
  const { pinStatus, tileId, legPremiums } = useOptionSelector((option) => {
    const premiumType = option.properties.premiumType;
    const legs = option.properties.legs;
    return {
      pinStatus: getPinStatus(option),
      tileId: option.properties.tileId,
      legPremiums: legs.map((leg) => {
        const legData = selectOptionQuoteLegs(option, leg.legId);
        return {
          legId: leg.legId,
          quotePremium: getPremiumFromQuoteLegs(legData, premiumType),
          legPremium: { bid: leg.premiumBid, ask: leg.premiumAsk },
        };
      }),
    };
  });

  function unpinAll() {
    const legProperties: NullableLegProperties[] = legPremiums.map((leg) => ({
      legId: leg.legId,
      premiumBid: null,
      premiumAsk: null,
    }));
    checkOption({ tileId, legProperties });
  }

  function pinAll() {
    const legProperties: NullableLegProperties[] = legPremiums
      .map((leg) => {
        // not yet pinned
        if (isNotDefined(leg.legPremium.bid) && isNotDefined(leg.legPremium.ask)) {
          return {
            legId: leg.legId,
            premiumBid: leg.quotePremium?.bid,
            premiumAsk: leg.quotePremium?.ask,
          };
        }
        return undefined;
      })
      .filter(isDefined);
    checkOption({ tileId, legProperties });
  }

  function handlePin() {
    switch (pinStatus) {
      case 'allPinned':
        unpinAll();
        return;
      case 'somePinned':
        pinAll();
        return;
      case 'nonePinned':
        pinAll();
        return;
    }
  }

  return (
    <Tooltip contents={<FormattedMessage id={getPinMessage(pinStatus)} />}>
      <Button
        data-e2e="PinAllLegsPremiums-button"
        size="sm"
        purpose="flat"
        color="secondary"
        iconPosition="start"
        disabled={pinStatus === 'disabled'}
        onClick={handlePin}
      >
        {pinStatus === 'allPinned' ? <UnpinIcon /> : <Icon icon="push_pin" />}
      </Button>
    </Tooltip>
  );
}

type PinStatus = 'disabled' | 'allPinned' | 'somePinned' | 'nonePinned';

function getPinStatus(option: FxOption): PinStatus {
  const pinnedPremiums = option.properties.legs.filter((leg) => isDefined(leg.premiumAsk) || isDefined(leg.premiumBid));
  const legs = option.properties.legs;
  const isStreaming = option.properties.rfs.status === 'PRICING';

  if (!isStreaming) {
    return 'disabled';
  }
  if (pinnedPremiums.length === legs.length) {
    return 'allPinned';
  }
  if (pinnedPremiums.length > 0) {
    return 'somePinned';
  }
  return 'nonePinned';
}

function getPinMessage(pinStatus: PinStatus): IntlMessageId {
  switch (pinStatus) {
    case 'disabled':
      return 'option.pin.premiums.pinAllDisabled';
    case 'allPinned':
      return 'option.pin.premiums.unpinAll';
    case 'somePinned':
      return 'option.pin.premiums.pinMore';
    case 'nonePinned':
      return 'option.pin.premiums.pinAll';
    default:
      assertUnreachable(pinStatus, `Unknown status ${pinStatus}`);
  }
}
